import { useEffect, useRef, useState } from "react";
import { X, Share, Plus } from "lucide-react";
import { promptInstall, canInstall, subscribeInstall } from "../lib/pwa";

const LS_NEVER = "habano.welcome.never";
const LS_INSTALL_DISMISSED = "habano.install.dismissed_at";

/**
 * Globally-mounted PWA install handler.
 *
 * • No explanation modal. Anywhere in the app that opens an `?install=1` deep
 *   link, fires `window.dispatchEvent(new CustomEvent("habano:install"))`,
 *   or clicks an element tagged `data-install-trigger` immediately attempts
 *   the native install prompt.
 * • Browsers with `beforeinstallprompt` (Chrome/Edge/Android) → fire it.
 * • iOS Safari has no prompt API → show a one-step bottom sheet ("Share → Add
 *   to Home Screen"). No paragraph text, just the icons.
 *
 * Replaces the old WelcomePopup which contained explanation copy and 4 buttons.
 */
export default function WelcomePopup() {
  const [iosOpen, setIosOpen] = useState(false);
  const [hasNative, setHasNative] = useState(canInstall());
  const unsub = useRef(null);

  // Keep an internal flag so we know whether to use native vs iOS sheet.
  useEffect(() => {
    unsub.current = subscribeInstall((v) => setHasNative(!!v));
    return () => unsub.current && unsub.current();
  }, []);

  const isStandalone = () =>
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true);

  const isIos = () =>
    typeof window !== "undefined" &&
    /iPad|iPhone|iPod/.test(window.navigator.userAgent) &&
    !window.MSStream;

  const trigger = async () => {
    if (isStandalone()) return; // already installed
    if (hasNative) {
      const outcome = await promptInstall();
      if (outcome === "accepted") {
        localStorage.setItem(LS_NEVER, "true");
      }
      return;
    }
    // iOS fallback — minimal one-step sheet
    if (isIos()) {
      setIosOpen(true);
      return;
    }
    // Desktop / unsupported — silently no-op
  };

  // Listen for the install event from any button anywhere in the app.
  useEffect(() => {
    const handler = () => trigger();
    window.addEventListener("habano:install", handler);
    document.addEventListener("click", (e) => {
      const el = e.target.closest?.("[data-install-trigger]");
      if (el) {
        e.preventDefault();
        trigger();
      }
    });
    return () => window.removeEventListener("habano:install", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNative]);

  // Auto-trigger when URL has `?install=1` — used by /join "Open in Habano App"
  useEffect(() => {
    if (typeof window === "undefined") return;
    const u = new URL(window.location.href);
    if (u.searchParams.get("install") === "1") {
      // Defer so the page paints first
      setTimeout(trigger, 600);
      u.searchParams.delete("install");
      window.history.replaceState({}, "", u.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNative]);

  if (!iosOpen) return null;

  return (
    <div
      role="dialog"
      data-testid="ios-install-sheet"
      aria-label="Add to Home Screen"
      className="fixed inset-0 z-[60] flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.85)" }}
      onClick={(e) => { if (e.target === e.currentTarget) { setIosOpen(false); localStorage.setItem(LS_INSTALL_DISMISSED, new Date().toISOString()); } }}
    >
      <div
        className="w-full max-w-md rounded-t-[28px] px-6 py-7"
        style={{
          background: "#171717",
          borderTop: "1px solid rgba(232,184,75,0.28)",
          boxShadow: "0 -20px 60px rgba(0,0,0,0.85)",
        }}
      >
        <button
          data-testid="ios-install-close"
          onClick={() => { setIosOpen(false); localStorage.setItem(LS_INSTALL_DISMISSED, new Date().toISOString()); }}
          aria-label="Close"
          className="absolute top-4 right-4 w-11 h-11 grid place-items-center rounded-full text-foreground/50 hover:text-gold"
        >
          <X className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <div className="flex items-center justify-center gap-6 text-foreground/90">
          <div className="flex flex-col items-center gap-1.5">
            <span className="w-12 h-12 grid place-items-center rounded-full border border-gold-soft text-gold">
              <Share className="w-5 h-5" strokeWidth={1.5} />
            </span>
            <span className="text-[11px] uppercase tracking-widest text-foreground/55">1. Share</span>
          </div>
          <span className="text-2xl text-gold">→</span>
          <div className="flex flex-col items-center gap-1.5">
            <span className="w-12 h-12 grid place-items-center rounded-full border border-gold-soft text-gold">
              <Plus className="w-5 h-5" strokeWidth={1.5} />
            </span>
            <span className="text-[11px] uppercase tracking-widest text-foreground/55">2. Add to Home Screen</span>
          </div>
        </div>
      </div>
    </div>
  );
}
